import { NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { OrderTypes } from '@next/core-lib/cart';
import { FilterConfig, FiltersConfig, FilterTypes, AgrLocationSummaryModule, AgrFilterModule } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { clearOrdersRequest, ordersRequest } from 'src/app/store/actions';
import { selectOrders } from 'src/app/store/selectors';
import { Environment } from 'src/environments/environment.model';

import { DateRange, Order, OrderColumnTypes, OrderFilterValues, OrderStatus } from '../../models/order.models';
import { OrdersTableComponent } from '../orders-table/orders-table.component';

@UntilDestroy()
@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, AgrLocationSummaryModule, AgrFilterModule, FormsModule, ReactiveFormsModule, OrdersTableComponent, AsyncPipe, AgrI18nSharedModule],
})
export class OrdersComponent implements OnInit, OnDestroy {
  location$ = this.store.select(UserSelectors.selectLocation);
  customer$ = this.store.select(UserSelectors.selectCustomer);
  orders$ = this.store.select(selectOrders);

  filteredOrders$ = new Subject<Order[]>();

  defaultDateRange$: Observable<DateRange> = of({
    startDate: this.defaultStartDate(),
    endDate: new Date(),
  });
  defaultOrderStatuses$ = of([
    {
      value: OrderStatus.Processing,
      viewValue: this.localeService.translate(`app.orders.status-${OrderStatus.Processing}`),
    },
    {
      value: OrderStatus.Finalized,
      viewValue: this.localeService.translate(`app.orders.status-${OrderStatus.Finalized}`),
    },
    {
      value: OrderStatus.Error,
      viewValue: this.localeService.translate(`app.orders.status-${OrderStatus.Error}`),
    },
  ]);
  defaultOrderStatuses = [OrderStatus.Processing, OrderStatus.Finalized, OrderStatus.Error];
  defaultOrderTypes = [
    OrderTypes.emergencyOrder,
    OrderTypes.preSalesOrder,
    OrderTypes.returnableOrder,
    OrderTypes.rushOrder,
    OrderTypes.salesOrder,
    OrderTypes.trustOrder,
    OrderTypes.pickUpOrder,
    OrderTypes.grainPreSalesOrder,
  ];
  defaultOrderTypes$ = of(
    this.defaultOrderTypes.map((type) => ({
      value: type,
      viewValue: this.localeService.translate(`app.order-types.${type.toLowerCase()}`),
    })),
  );

  public filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      search: {
        type: FilterTypes.Text,
        defaultValue: of(''),
      },
      range: {
        type: FilterTypes.Daterange,
        defaultValue: this.defaultDateRange$,
      },
      status: {
        type: FilterTypes.MultiSelect,
        label: this.localeService.translate('app.orders.status-label'),
        options: this.defaultOrderStatuses$,
        value: of(this.defaultOrderStatuses),
        defaultValue: of(this.defaultOrderStatuses),
      },
    },
  };
  public filterControl: FormControl = new FormControl();

  constructor(
    private readonly localeService: LocaleService,
    private router: Router,
    private readonly store: Store<RootState>,
    @Inject(EnvironmentInjector) private readonly environment: Environment,
  ) {
    this.store.dispatch(ordersRequest({}));
  }

  ngOnInit(): void {
    combineLatest([this.orders$, this.filterControl.valueChanges])
      .pipe(untilDestroyed(this))
      .subscribe(([orders, filterValues]: [Order[], OrderFilterValues]) => {
        this.filteredOrders$.next(this.filterOrders(orders, filterValues));
      });

    this.filterConfig.filters.orderType = {
      type: FilterTypes.MultiSelect,
      label: this.localeService.translate(`app.orders-table.columns.${OrderColumnTypes.orderType}`),
      options: this.defaultOrderTypes$,
      value: of(this.defaultOrderTypes),
      defaultValue: of(this.defaultOrderTypes),
    };
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearOrdersRequest());
  }

  private filterOrders(orders: Order[], filters: OrderFilterValues): Order[] {
    if (!orders?.length || !filters) {
      return [];
    }

    let filteredOrders = orders.filter(
      (order: Order) =>
        !order.createdOn?.length ||
        (new Date(order.createdOn).getTime() >= filters.range.startDate.getTime() && new Date(order.createdOn).getTime() <= filters.range.endDate.getTime()),
    );

    if (filters.search?.trim().length > 0) {
      filteredOrders = filteredOrders.filter((order: Order) => order.orderNumbers?.join(',').includes(filters.search.trim()));
    }
    if (filters.status?.length > 0) {
      filteredOrders = filteredOrders.filter((order: Order) => !order.orderStatus?.length || filters.status.includes(order.orderStatus));
    }

    if (filters.orderType?.length > 0) {
      filteredOrders = filteredOrders.filter((order: Order) => !order.orderType?.length || filters.orderType.includes(order.orderType));
    }

    return filteredOrders;
  }

  // The default start date is the current date minus 12 months
  private defaultStartDate(): Date {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 12);
    return startDate;
  }
}
